import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../config/firestore';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth } from '../../config/firebase';
import { createNotification } from '../../services/notificationService';

function CreateGroup() {
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [emails, setEmails] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in to create a group');

      const groupData = {
        name: groupName,
        description: description,
        createdBy: user.uid,
        members: {
            [user.uid]: {
              status: true,
              lastUpdated: new Date(),
              displayName: user.displayName,
              email: user.email
            }
          },
        created: new Date()
      };

      const docRef = await addDoc(collection(db, 'groups'), groupData);
      console.log("Group created with ID: ", docRef.id);

      // Create notification for group creation
      await createNotification(
        user.uid,
        'group',
        'created',
        `You created a new group: ${groupName}`,
        {
          relatedGroupId: docRef.id,
          relatedGroupName: groupName
        }
      );

      navigate('/dashboard');
    } catch (err) {
      setError('Failed to create group: ' + err.message);
      console.error('Error creating group:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto pt-10">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-gray-600 hover:text-gray-900 -ml-2 flex items-center"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to Dashboard
        </Button>

        <Card className="w-full">
          <CardHeader className="space-y-4">
            <CardTitle className="text-2xl text-center">Create New Group</CardTitle>
            <p className="text-sm text-gray-500 text-center">Add details for your new group</p>
          </CardHeader>

          <CardContent className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name
                </label>
                <Input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 h-24 resize-none"
                  placeholder="Add a description for your group"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invite Members (Optional)
                </label>
                <Input
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  placeholder="Enter email addresses"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
              </div>

              <div className="pt-4">
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#14171F] hover:bg-gray-800 text-white rounded-xl py-3"
                >
                  {isLoading ? 'Creating...' : 'Create Group'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CreateGroup;